import React, { useEffect, useMemo, useRef, useState } from "react";

import { keyBy } from "lodash";
import {
  Control,
  Controller,
  FormProvider,
  useForm,
  useWatch,
} from "react-hook-form";

import { Flow, FlowVersion } from "src/api/flowTypes";
import { useFlow } from "src/api/queries";
import { FieldErrors } from "src/api/types";
import { AutomapButton } from "src/auto-mapping/AutomapButton";
import { Card } from "src/base-components/Card";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { LoadingView } from "src/base-components/LoadingView";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import { useRevision } from "src/changeHistory/queries";
import { AutoMappingResponse } from "src/clients/copilot";
import { LoopNode, LoopNodeData } from "src/constants/NodeDataTypes";
import { useSchemas } from "src/datasets/utils";
import { Accordion } from "src/design-system/Accordion";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import {
  tracker,
  trackingEvents,
} from "src/instrumentation/customTrackingEvents";
import { completionQueryKeys } from "src/nodeEditor/api/queries";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { AdvancedSettings } from "src/parentFlowNodes/loopNode/AdvancedSettings";
import { ChildFlowNotAvailable } from "src/parentFlowNodes/loopNode/ChildNotAvailable";
import { FlowSelectionModal } from "src/parentFlowNodes/loopNode/FlowSelectionModal";
import { InputOutputMappings } from "src/parentFlowNodes/loopNode/InputOutputMappings";
import { NoDecisionFlowSelected } from "src/parentFlowNodes/loopNode/NoDecisionFlowSelected";
import { SelectedVersionDisplay } from "src/parentFlowNodes/loopNode/SelectedVersionDisplay";
import { useHighlightFields } from "src/parentFlowNodes/useHighlightFields";
import { useUpdateKnownEtag } from "src/parentFlowNodes/useUpdateKnownEtag";
import {
  queryErrorLikelyBecauseChildFlowGotDeleted,
  setupLoopNodeForm,
} from "src/parentFlowNodes/utils";
import { queryClient } from "src/queryClient";
import { ErrorContent } from "src/router/ErrorPage";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { convertFieldErrorsBeToFe } from "src/utils/FieldErrorUtils";
import { logger } from "src/utils/logger";
import { useSubmitForm } from "src/utils/useSubmitForm";

type Props = {
  selectedNode: LoopNode;
  resultsOutdated?: boolean;
  lazyInputs: boolean;
} & NodeEditorBaseProps<LoopNodeData>;

export type LoopNodeForm = LoopNodeData;

const useRequestNewTypingData = (
  nodeId: string,
  control: Control<LoopNodeForm>,
) => {
  /*
   * We need to invalidate the query when the target_list_expression changes
   * because the typing data is based on the target list
   */
  const target_list = useWatch({
    control: control,
    name: "target_list_expression.value",
  });

  const { version } = useAuthoringContext();
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: completionQueryKeys.node(version.id, nodeId),
    });
  }, [nodeId, target_list, version.id]);
};

export const LoopNodeEditor: React.FC<Props> = ({
  selectedNode,
  isReactive,
  onUpdate,
  immutable,
  displayedError,
  lazyInputs,
  resultsOutdated = false,
}) => {
  const { version, workspace } = useAuthoringContext();
  const { renderedInDiffView } = useDiffViewContext();
  const fieldErrors: FieldErrors | undefined = displayedError?.field_errors
    ? convertFieldErrorsBeToFe(displayedError.field_errors, true)
    : undefined;

  const nodeData = selectedNode.data;
  const childFlowQuery = useFlow(nodeData.child_flow_id ?? "", {
    refetchInterval: 5000,
  });
  const childVersionRevisionQuery = useRevision({
    versionId: selectedNode.data.child_flow_version_id ?? undefined,
    etag: selectedNode.data.child_flow_version_known_etag ?? undefined,
    keepPreviousData: true,
  });
  const childVersionSchemas = useSchemas(
    childFlowQuery.data?.versions.find(
      (version) => version.id === selectedNode.data.child_flow_version_id,
    ),
  );
  const handleSubmitRef = useRef<VoidFunction>(() => {});
  const isReactiveRef = useRef(isReactive);
  const { highlightedFields, highlightInputFields } = useHighlightFields();

  const [defaultValues, schemaMappingErrors, fieldsToRemove] = useMemo(() => {
    // Only setup the form once the child flow has been fetched
    if (immutable || childFlowQuery.data === undefined) {
      return [nodeData, [], []];
    } else {
      return setupLoopNodeForm(nodeData, childVersionSchemas);
    }
  }, [immutable, childFlowQuery.data, nodeData, childVersionSchemas]);

  const inputMappingErrors = schemaMappingErrors.filter(
    (e) => e.type === "input",
  );

  const formMethods = useForm<LoopNodeForm>({
    defaultValues,
    ...(isReactive && { values: defaultValues }),
  });

  const targetListId = formMethods.getValues("target_list_expression.id");
  const isTargetListHighlighted = useIsFieldHighlighted(targetListId);

  const handleAutoMapping = (response: AutoMappingResponse) => {
    const currentInputMappings = formMethods.getValues("input_mappings");

    const updatedInputMappings = { ...currentInputMappings };
    const inputFields = [] as string[];

    response.mappings.forEach((mapping) => {
      const inputFieldId = Object.keys(currentInputMappings).find(
        (id) => currentInputMappings[id].destination === mapping.destination,
      );

      if (inputFieldId) {
        updatedInputMappings[inputFieldId] = {
          ...updatedInputMappings[inputFieldId],
          source: mapping.source,
        };
        inputFields.push(mapping.destination);
      }
    });

    // needs to be ref as the callback doesn't update once the response is received
    if (!isReactiveRef.current) {
      highlightInputFields(inputFields);
      formMethods.setValue("input_mappings", updatedInputMappings, {
        shouldTouch: true,
      });

      tracker.emit(
        trackingEvents.flowNodeApplyAutomap({
          flow_version_id: version.id,
          node_id: selectedNode.id,
          etag: version.etag ?? "",
          is_loop_node: true,
          automapping_id: response.id,
          organization_id: workspace.organization_id,
        }),
      );
    } else {
      logger.warn("Unable to apply auto-mapping in reactive mode", response);
    }
  };

  // keep refs up to date
  handleSubmitRef.current = formMethods.handleSubmit((data) => {
    onUpdate({
      nodeId: selectedNode.id,
      newData: {
        ...nodeData,
        ...data,
      },
    });
  });

  const { unregister } = formMethods;
  useEffect(() => {
    // Prevent unnecessary submits because they e.g. result in change history entries
    if (fieldsToRemove.length > 0) {
      unregister(fieldsToRemove);
      if (!isReactive && !immutable) {
        handleSubmitRef.current();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsToRemove.join(","), isReactive, unregister, immutable]);

  useEffect(() => {
    isReactiveRef.current = isReactive;
  }, [isReactive]);

  useUpdateKnownEtag({
    nodeId: selectedNode.id,
    nodeData,
    immutable,
    isReactive,
    onUpdate,
    etag: childFlowQuery.data?.versions.find(
      (version) => version.id === selectedNode.data.child_flow_version_id,
    )?.etag,
  });

  const [flowSelectionOpen, setFlowSelectionOpen] = useState(false);

  const { watch } = formMethods;

  useSubmitForm({
    onChange: (data: LoopNodeForm) => {
      onUpdate({ newData: { ...selectedNode.data, ...data } });
    },
    disabled: isReactive,
    previousValues: defaultValues,
    watch: watch,
  });
  useRequestNewTypingData(selectedNode.id, formMethods.control);

  const [childFlowId, childFlowVersionId, targetListExpression] =
    formMethods.watch([
      "child_flow_id",
      "child_flow_version_id",
      "target_list_expression.value",
    ]);

  const noFlowSelected = !childFlowId || !childFlowVersionId;

  return (
    <FormProvider {...formMethods}>
      <div className="mt-4 px-6">
        <Accordion
          defaultValue={[
            "list",
            "decisionFlow",
            "mappings",
            "advancedSettings",
          ]}
        >
          <Accordion.Item
            readOnly={renderedInDiffView}
            title="Select the list you want to loop over"
            value="list"
          >
            <Card>
              <Card.Title title="Loop over every item in" />
              <Card.Content className="mt-2">
                <Controller
                  control={formMethods.control}
                  name="target_list_expression.value"
                  render={({ field }) => (
                    <AutocompleteCodeInput
                      dataLoc="target-list-expression"
                      disabled={immutable}
                      isSearchHighlighted={isTargetListHighlighted}
                      placeholder="data.field"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Card.Content>
            </Card>
          </Accordion.Item>
          <Accordion.Item
            dataLoc="select-decision-flow"
            readOnly={renderedInDiffView}
            title="Select the Decision Flow you want to apply"
            value="decisionFlow"
          >
            {noFlowSelected ? (
              <NoDecisionFlowSelected
                handleSelectDecisionFlow={() => setFlowSelectionOpen(true)}
                immutable={immutable}
              />
            ) : (
              <LoadingView
                queryResult={childFlowQuery}
                renderErrored={(message) => {
                  if (queryErrorLikelyBecauseChildFlowGotDeleted(message))
                    return (
                      <ChildFlowNotAvailable
                        handleSelectDecisionFlow={() =>
                          setFlowSelectionOpen(true)
                        }
                        immutable={immutable}
                      />
                    );
                  else return <ErrorContent message={message} />;
                }}
                renderUpdated={(childFlow: Flow) => {
                  if (
                    !childFlow.versions.some(
                      (version) =>
                        version.id ===
                        formMethods.getValues().child_flow_version_id,
                    )
                  )
                    return (
                      <ChildFlowNotAvailable
                        handleSelectDecisionFlow={() =>
                          setFlowSelectionOpen(true)
                        }
                        immutable={immutable}
                      />
                    );
                  return (
                    <SelectedVersionDisplay
                      childFlow={childFlow}
                      childFlowVersionId={
                        formMethods.getValues().child_flow_version_id
                      }
                      handleChangeDecisionFlow={() =>
                        setFlowSelectionOpen(true)
                      }
                      immutable={immutable}
                    />
                  );
                }}
              />
            )}
          </Accordion.Item>
          <Accordion.Item
            dataLoc="mappings"
            readOnly={renderedInDiffView}
            rightSlot={
              !noFlowSelected ? (
                <AutomapButton
                  disabled={immutable || targetListExpression === ""}
                  nodeId={selectedNode.id}
                  tooltipTitle={(() => {
                    if (targetListExpression === "") {
                      return "Fill the item information above to be able to Auto-map.";
                    }

                    return resultsOutdated
                      ? "Auto-mapping uses information from the flow schemas, as well as typing information from test runs. Initiate a test run for the latest typing information"
                      : "";
                  })()}
                  type="flow_loop"
                  onAutoMapping={handleAutoMapping}
                />
              ) : undefined
            }
            title="Map inputs and outputs of Decision Flows"
            value="mappings"
          >
            {noFlowSelected ? (
              <NoDecisionFlowSelected
                handleSelectDecisionFlow={() => setFlowSelectionOpen(true)}
                immutable={immutable}
              />
            ) : (
              <InputOutputMappings
                childFlowQuery={childFlowQuery}
                childVersionRevisionQuery={childVersionRevisionQuery}
                fieldErrors={fieldErrors}
                fieldMappingErrors={keyBy(
                  inputMappingErrors.filter(
                    (error) => error.issue === "field-missing",
                  ),
                  "fieldName",
                )}
                highlightedFields={highlightedFields.input}
                immutable={immutable}
                lazyInputs={lazyInputs}
                setFlowSelectionOpen={setFlowSelectionOpen}
              />
            )}
          </Accordion.Item>
          <Accordion.Item
            readOnly={renderedInDiffView}
            title="Advanced settings"
            value="advancedSettings"
          >
            <AdvancedSettings
              firstOutputFieldName={
                childVersionSchemas.output?.properties[0]?.fieldName
              }
              immutable={immutable}
            />
          </Accordion.Item>
        </Accordion>
      </div>
      <FlowSelectionModal
        handleClose={() => setFlowSelectionOpen(false)}
        handleSubmit={(version: FlowVersion, flow: Flow) => {
          // When a flow version is selected we want to immediately update the node (and not rely on the form submit handler to do it later, because we might not have a lock acquired)
          const newData = {
            ...selectedNode.data,
            ...formMethods.getValues(),
            child_flow_slug: flow.slug,
            child_flow_version_known_etag: version.etag,
            child_flow_id: flow.id,
            child_flow_version_id: version.id,
            is_stale: false,
          };
          onUpdate({
            newData,
          });
        }}
        open={flowSelectionOpen}
      />
    </FormProvider>
  );
};
